import { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { mindtimeWazuhMitreApi } from 'src/api/mindtime/wazuhMitreApi';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhMitreQuery,
  WazuhMitreSummaryData,
  WazuhMitreTechniqueItem,
} from 'src/types/mindtime/wazuhMitre';
import type { WazuhPagedData, WazuhScopeParams } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { buildEdrApiScope } from '../shared/wazuhScopeParams';

export type MitreFacets = {
  tactics: string[];
  techniques: string[];
  mitreIds: string[];
};

export type MitreFilters = {
  tactic?: string;
  technique?: string;
  mitreId?: string;
  severityBand?: string;
  wazuhAgentId?: string;
  organizationId?: string;
};

export type MitreState = {
  loading: boolean;
  summary: WazuhMitreSummaryData | null;
  items: WazuhPagedData<WazuhMitreTechniqueItem> | null;
  topTechniques: WazuhMitreTechniqueItem[];
  facets: MitreFacets;
};

type BuildMitreQueryInput = {
  scope: WazuhScopeParams;
  profileOrgId?: string;
  isReseller: boolean;
  search?: string;
  filters?: MitreFilters;
  page?: number;
  pageSize?: number;
};

const emptyFacets = (): MitreFacets => ({
  tactics: [],
  techniques: [],
  mitreIds: [],
});

const emptyState = (): MitreState => ({
  loading: true,
  summary: null,
  items: null,
  topTechniques: [],
  facets: emptyFacets(),
});

const mergeFacets = (
  items: WazuhMitreTechniqueItem[],
  previous: MitreFacets
): MitreFacets => {
  const tactics = new Set(previous.tactics);
  const techniques = new Set(previous.techniques);
  const mitreIds = new Set(previous.mitreIds);

  items.forEach((item) => {
    if (item.tactic) tactics.add(item.tactic);
    if (item.techniqueName) techniques.add(item.techniqueName);
    if (item.mitreId) mitreIds.add(item.mitreId);
  });

  return {
    tactics: Array.from(tactics).sort((a, b) => a.localeCompare(b)),
    techniques: Array.from(techniques).sort((a, b) => a.localeCompare(b)),
    mitreIds: Array.from(mitreIds).sort((a, b) => a.localeCompare(b)),
  };
};

const buildMitreQuery = ({
  scope,
  profileOrgId,
  isReseller,
  search = '',
  filters = {},
  page,
  pageSize,
}: BuildMitreQueryInput): WazuhMitreQuery => {
  const { tactic, technique, mitreId, severityBand, wazuhAgentId, organizationId } = filters;
  const effectiveScope = buildEdrApiScope({
    scope,
    profileOrgId,
    isReseller,
    pageOrganizationId: organizationId || undefined,
  });

  return {
    ...effectiveScope,
    ...(typeof page === 'number' ? { page } : {}),
    ...(typeof pageSize === 'number' ? { pageSize } : {}),
    ...(search ? { search } : {}),
    ...(tactic ? { tactic } : {}),
    ...(technique ? { technique } : {}),
    ...(mitreId ? { mitreId } : {}),
    ...(severityBand ? { severityBand } : {}),
    ...(wazuhAgentId ? { wazuhAgentId } : {}),
  };
};

const triggerBlobDownload = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const useMitreExport = (search = '', filters: MitreFilters = {}) => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(() => {
    setExporting(true);
    mindtimeWazuhMitreApi
      .export(buildMitreQuery({ scope, profileOrgId, isReseller, search, filters }))
      .then(({ blob, filename }) => {
        const name = filename?.trim() ? filename : 'mitre_techniques_export.xlsx';
        triggerBlobDownload(blob, name);
        toast.success('Export downloaded');
      })
      .catch((error) => {
        logger(error, 'error', 'useMitreExport');
        toast.error(error instanceof Error ? error.message : 'Export failed');
      })
      .finally(() => {
        setExporting(false);
      });
  }, [scope, profileOrgId, isReseller, search, filters]);

  return { exporting, handleExport };
};

export const useMitreData = (
  page = 0,
  pageSize = 10,
  search = '',
  filters: MitreFilters = {}
): MitreState => {
  const { scope, isReseller } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const [state, setState] = useState<MitreState>(emptyState);

  const scopeKey = useMemo(
    () =>
      JSON.stringify({
        resellerId: scope.resellerId,
        startDate: scope.startDate,
        endDate: scope.endDate,
        isReseller,
        organizationId: filters.organizationId || '',
      }),
    [scope.resellerId, scope.startDate, scope.endDate, isReseller, filters.organizationId]
  );

  const { tactic, technique, mitreId, severityBand, wazuhAgentId, organizationId } = filters;

  useEffect(() => {
    setState((prev) => ({ ...prev, facets: emptyFacets() }));
  }, [scopeKey]);

  useEffect(() => {
    let cancelled = false;

    const effectiveScope = buildEdrApiScope({
      scope,
      profileOrgId,
      isReseller,
      pageOrganizationId: organizationId || undefined,
    });

    Promise.all([
      mindtimeWazuhMitreApi.getSummary(effectiveScope),
      mindtimeWazuhMitreApi.getTopTechniques({ ...effectiveScope, limit: 5 }),
    ])
      .then(([summary, topTechniques]) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          summary,
          topTechniques,
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMitreData.summary');
          setState((prev) => ({
            ...prev,
            summary: null,
            topTechniques: [],
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, isReseller, organizationId]);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    mindtimeWazuhMitreApi
      .getTechniques(
        buildMitreQuery({
          scope,
          profileOrgId,
          isReseller,
          search,
          filters,
          page,
          pageSize,
        })
      )
      .then((items) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          loading: false,
          items,
          facets: mergeFacets(items.items, prev.facets),
        }));
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useMitreData.techniques');
          setState((prev) => ({
            ...prev,
            loading: false,
            items: null,
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    scope,
    profileOrgId,
    isReseller,
    page,
    pageSize,
    search,
    tactic,
    technique,
    mitreId,
    severityBand,
    wazuhAgentId,
    organizationId,
  ]);

  return state;
};
